// A file attached to an assistant response, as sent by the chat backend's
// `files` array — both live WebSocket responses and GET /fetch_chat_history
// rows use this same shape. Covers both previewable documents (pdf/docx/
// xlsx/csv, see DocPreview/) and generated images (see GeneratedImage.tsx):
// both are resolved to a usable URL through the same GET /adls/sas_url
// endpoint, given `path` (see services/api/storage.api.ts).
//
// file_id is nullable — history rows can come back without one (unlike live
// WS responses, which always set it) — so `path` is the identity used for
// keys/caching/"is this the active one" comparisons everywhere, never
// file_id.
export interface ChatResponseFile {
  file_id: string | null;
  file_name: string | null;
  file_type: string;
  path: string;
}

export function isImageFileType(fileType: string): boolean {
  return fileType.startsWith('image/');
}

export interface Message {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: string;
  isMarkdown?: boolean;
  isFormatted?: boolean;
  isDeepResearchReport?: boolean;
  files?: ChatResponseFile[];
  turnId?: string | null;
}


export interface ClarificationQuestion {
  question: string;
  options: string[];
}

export interface FocusItem {
  id?: string;
  item_id?: string;
  folder_id?: string;
  file_id?: string;
  folderId?: string;
  fileId?: string;
  name?: string;
  folder_name?: string;
  file_name?: string;
  type?: string;
  item_type?: string;
  is_folder?: boolean;
}

export interface FocusPathItem {
  id: string;
  name: string;
}

// ── Report form (backend-driven dynamic renderer) ───────────────────────────
// A new report type needs zero frontend changes — these shapes mirror the
// report API and the keys in field_type_reference.md (report_generation_poc).
// Every key past field_key/field_label/display_order/type is optional.
export interface ReportTypeSummary {
  report_id: number;
  report_key: string;
  report_name: string;
}

export interface ReportFieldOption {
  value: string | number;
  label: string;
}

export interface ReportFieldValidation {
  max_length?: number;
  pattern?: string;
  min?: number;
  max?: number;
  step?: number;
}

// text | number | boolean | choice | multi_choice. Kept a plain string: an
// unknown type renders as a text input (see FieldRenderer) instead of failing.
export type ReportFieldType = string;

export interface ReportField {
  field_key: string;
  field_label: string;
  display_order: number;
  type: ReportFieldType;
  display_style?: string;
  required?: boolean;
  options?: ReportFieldOption[];
  placeholder?: string;
  help_text?: string;
  default_value?: ReportFieldValue;
  validation?: ReportFieldValidation;
}

// The form for one report. The name isn't in it: the UI already has it from
// the report list (ReportTypeSummary).
export interface ReportSchema {
  report_id: number;
  fields: ReportField[];
}

export type ReportFieldValue = string | number | boolean | Array<string | number | boolean> | null;

export type ReportFormValues = Record<string, ReportFieldValue>;

// Sent as `report_generation` on the chat websocket message (see chatPayload).
export interface ReportGeneration {
  report_id: number;
  values: ReportFormValues;
}